import { Button, Divider, Header, Layout, TheVoice } from "@obs/base";
import { PrintIcon } from "@obs/icons";
import { useSignals } from "@preact/signals-react/runtime";
import { Link } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { useRef } from "react";
import { useIntl } from "react-intl";
import { useReactToPrint } from "react-to-print";

import { QrPoster } from "../Components/Qr/QrPoster";
import { DisplayModeRadio } from "../Components/Settings/DisplayModeRadio";
import { LanguagesInputs } from "../Components/Settings/LanguagesInputs";
import { MaxNumberOfTripsRadio } from "../Components/Settings/MaxNumberOfTripsRadio";
import { MaxTimeWindowRadio } from "../Components/Settings/MaxTimeWindowRadio";
import { MinTimeWindowRadio } from "../Components/Settings/MinTimeWindowRadio";
import { SelectStation } from "../Components/Settings/SelectStation";
import {
	displayMode,
	searchParamsSettings,
	stationCode,
} from "../Components/Settings/settingsState";
import { TimeFormatRadio } from "../Components/Settings/TimeFormatRadio";
import { Station } from "../generated/openapi-codegen/ob-software/station-display/station-display/models/Station";
import { useGetStations } from "../Hooks/useGetStations";
import { getDisplayModeMessage } from "../intl/enums";

export const Route = createFileRoute("/")({
	component: RouteComponent,
});

function RouteComponent() {
	useSignals();
	const { formatMessage } = useIntl();
	const posterRef = useRef<HTMLDivElement>(null);
	const printPoster = useReactToPrint({
		contentRef: posterRef,
		documentTitle: `${stationCode.value}-Station-Display`,
	});

	const { data: station } = useGetStations<Station | undefined>({
		select: (data) => data.stations.find((s) => s.uic_code === stationCode.value),
	});

	const trackingUrl = stationCode.value
		? `${window.location.origin}/${stationCode.value}?${new URLSearchParams(searchParamsSettings.value as Record<string, string>).toString()}`
		: "";

	return (
		<>
			<Header.Web />
			<Layout.Container maxWidth="sm" render={<main />}>
				<TheVoice>
					{formatMessage({
						id: "settings.title",
						defaultMessage: "Settings",
					})}
				</TheVoice>
				<LanguagesInputs />
				<Divider.Section />
				<SelectStation />
				<Divider.Section />
				<DisplayModeRadio />
				<Divider.Section />
				<TimeFormatRadio />
				<Divider.Section />
				<MinTimeWindowRadio />
				<Divider.Section />
				<MaxTimeWindowRadio />
				<Divider.Section />
				<MaxNumberOfTripsRadio />
				<Layout.Box.Default>
					<Button.Primary
						nativeButton={false}
						render={
							<Link
								to="/$stationCode"
								params={{ stationCode: stationCode.value || "" }}
								search={searchParamsSettings.value}
							/>
						}
						disabled={!stationCode.value}
					>
						{formatMessage(
							{
								id: "settings.display.button",
								defaultMessage: `Show {displayMode}`,
							},
							{
								displayMode: formatMessage(getDisplayModeMessage(displayMode.value)),
							},
						)}
					</Button.Primary>
					<Button.Secondary disabled={!stationCode.value} icon={{ Component: PrintIcon }} onClick={printPoster}>
						{formatMessage({
							id: "station_display.settings.button.print_qr",
							defaultMessage: "Print QR code poster",
							description:
								"Secondary button on the settings page. Opens the print dialog for the QR poster without navigating. Keep short — fits a standard button with a printer icon.",
						})}
					</Button.Secondary>
				</Layout.Box.Default>
				<div style={{ display: "none" }}>
					<QrPoster ref={posterRef} stationName={station?.localized_name ?? ""} trackingUrl={trackingUrl} />
				</div>
			</Layout.Container>
		</>
	);
}
